import React, { useCallback, useState } from "react";
import { connect } from "react-redux";
import _ from "lodash";
import classes from "./FeedbackTable.scss";
import classNames from "classnames";

import { useI18n } from "Hooks";

import FeedbackInput from "./components/FeedbackInput";
import {
  handleAssessmentToolResponse,
  updateToolRemarks,
} from "./modules/FeedbackTableModules";
import { getUserType } from "modules/CommonSelectors";
import { isUserTypeStudentOrParent } from "Utils";

const FeedbackTable = ({
  feedbackToolData,
  projectToolResponse,
  disabled,
  projectId,
  handleAssessmentToolResponse,
  ibPYPElementRemarks,
  updateToolRemarks,
  projectDetails: { studentsList },
  isUserStudentOrParent,
}) => {
  const [selectedCriteria, setSelectedCriteria] = useState(
    _.chain(projectToolResponse)
      .filter(item => item?.columnId)
      .map(item => ({ [item?.rowId]: item?.columnId }))
      .value()
  );
  const { t } = useI18n(["common", "project"]);

  const debouncedUpdateToolResponse = useCallback(
    _.debounce(params => {
      updateToolRemarks(params);
    }, 500),
    []
  );

  const { id: studentId } = studentsList[0] || {};

  const getHeaders = () => {
    const levelCount = 3;
    return (
      <thead>
        <tr>
          <th
            className={classNames(
              classes.tableHeader,
              "text-label",
              classes.firstHeader,
              classes.stickyHeader,
              classes.stickyFirstColumn
            )}
          >
            {t("common:criteria")}
          </th>
          {_.times(levelCount, index => (
            <th
              key={`level-${index + 1}`}
              className={classNames(
                classes.tableHeader,
                "text-label",
                classes.stickyHeader
              )}
            >
              {t("common:level_with_count", {
                count: index + 1,
              })}
            </th>
          ))}
          <th
            className={classNames(
              classes.tableHeader,
              "text-label",
              classes.feedbackHeader,
              classes.stickyHeader
            )}
          >
            {t("common:feedback")}
          </th>
        </tr>
      </thead>
    );
  };

  const getSelectedDescriptorStyles = ({ descriptor, criteriaId }) => {
    const isSelected = _.find(selectedCriteria, item => {
      return item[criteriaId] === descriptor.id;
    });

    const {
      achievementLevel: { label: achievementLabel },
    } = descriptor;
    if (isSelected) {
      switch (achievementLabel) {
        case "1": {
          return {
            selectedClass: classes.selectedDescriptorLevel1,
            dotClass: classes.dotRed,
          };
        }
        case "2": {
          return {
            selectedClass: classes.selectedDescriptorLevel2,
            dotClass: classes.dotYellow,
          };
        }
        case "3": {
          return {
            selectedClass: classes.selectedDescriptorLevel3,
            dotClass: classes.dotGreen,
          };
        }
      }
    }
    return {};
  };

  const onCellClick = ({ rowId, columnId }) => {
    if (disabled) {
      return;
    }
    let setColumnIdNull = false;
    setSelectedCriteria(prevState => {
      const { isPresent, isSelected } = _.reduce(
        prevState,
        (acc, item) => {
          const key = _.keys(item)[0];
          if (key === rowId) {
            acc.isPresent = true;
            acc.isSelected = item[key] === columnId;
          }
          return acc;
        },
        { isPresent: false, isSelected: false }
      );
      if (isSelected) {
        // deselect the descriptor

        setColumnIdNull = true;
        return _.filter(prevState, item => item[rowId] !== columnId);
      } else if (isPresent) {
        // update the existing descriptor

        return _.map(prevState, item => {
          const key = _.keys(item)[0];
          return key === rowId ? { [rowId]: columnId } : item;
        });
      } else {
        // select the descriptor

        return [...prevState, { [rowId]: columnId }];
      }
    });

    handleAssessmentToolResponse({
      parentId: projectId,
      parentType: "PROJECT",
      rowId,
      columnId: setColumnIdNull ? null : columnId,
      type: "MYP_OBJECTIVE_RUBRIC",
    });
  };

  const getTableBody = ({ objectiveRubricDescriptors, criteriaId }) => {
    return _.map(objectiveRubricDescriptors, descriptor => {
      const { label, id } = descriptor;
      const { selectedClass, dotClass } = getSelectedDescriptorStyles({
        descriptor,
        criteriaId,
      });

      const tableCellContentClasses = classNames({
        ["text-body"]: true,
        [classes.tableCellContent]: true,
        [classes.tableCellContentHover]: !disabled && !selectedClass,
        [selectedClass]: selectedClass,
        [classes.tableCellDisabled]: disabled,
      });
      return (
        <td
          id={id}
          valign="top"
          className={tableCellContentClasses}
          onClick={() => {
            onCellClick({
              rowId: criteriaId,
              columnId: id,
            });
          }}
        >
          <div>
            <div dangerouslySetInnerHTML={{ __html: label }}></div>
            {dotClass ? (
              <div className={classNames(classes.dotContainer, dotClass)}></div>
            ) : null}
          </div>
        </td>
      );
    });
  };

  return (
    <div className={classes.container}>
      <div className={"text-label"}>
        {t("common:supervisor_with_label_suffix", {
          label: t("common:feedback"),
        })}
      </div>
      <div className={classes.tableWrapper}>
        <table className={classes.tableInnerContainer}>
          {getHeaders()}
          <tbody>
            {_.map(feedbackToolData?.criteria, item => {
              const { label, objectiveRubricDescriptors, id } = item;

              const { message: feedback } =
                _.find(
                  ibPYPElementRemarks,
                  response => response.ibPYPElementId === id
                ) || {};

              const columnId = _.get(
                _.find(selectedCriteria, item => _.has(item, id)),
                id
              );

              return (
                <tr>
                  <td
                    id={id}
                    valign="top"
                    className={classNames(
                      classes.criteriaLabel,
                      "text-body",
                      classes.stickyFirstColumn
                    )}
                  >
                    <div
                      className={classNames(classes.tableCellContent, {
                        [classes.tableCellDisabled]: disabled,
                      })}
                    >
                      {label}
                    </div>
                  </td>
                  {getTableBody({ objectiveRubricDescriptors, criteriaId: id })}
                  <td className={classes.feedbackCell}>
                    <FeedbackInput
                      disabled={disabled}
                      projectId={projectId}
                      criteriaId={id}
                      feedback={feedback}
                      debouncedUpdateSupervisorFeedback={
                        debouncedUpdateToolResponse
                      }
                      columnId={columnId}
                      t={t}
                      studentId={studentId}
                      isUserStudentOrParent={isUserStudentOrParent}
                    />
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
};

const mapActionCreators = {
  handleAssessmentToolResponse,
  updateToolRemarks,
};

const mapStateToProps = (state, ownProps) => {
  const { disabled: isDisabled } = ownProps;

  const userType = getUserType(state);
  const isUserStudentOrParent = isUserTypeStudentOrParent({ userType });

  return {
    isUserStudentOrParent,
    disabled: isDisabled || isUserStudentOrParent,
  };
};

export default connect(mapStateToProps, mapActionCreators)(FeedbackTable);
